import Link from "next/link";
import type { ReactNode } from "react";

import { LogoOI } from "@/components/common/LogoOI";
import { ETICHETTA_ACCESA, FUOCO_HOME, RIMANDO } from "@/components/home/stile";
import { SegnoTitolo } from "@/components/home/TestaSezione";
import type { NomeIcona } from "@/components/home/Icona";
import { AltriServiziARotazione } from "@/components/servizi/catalogo/AltriServiziARotazione";
import { RiquadroIcona } from "@/components/servizi/catalogo/CardServizio";
import { Rombi } from "@/components/servizi/monitoraggio/Rombi";
import { NOTA_PASSO, NUMERO_PASSO, PULSANTE, RIQUADRO_FASE } from "@/components/servizi/monitoraggio/stile";
import { AREE_PAGINA, TOTALE_SERVIZI } from "@/lib/serviziPagina";
import { PRIMA_SCELTA_ALTRI, SERVIZI_CATALOGO } from "@/lib/sorteggio-servizi";

/* I pezzi che /servizi/monitoraggio, /servizi/report,
   /servizi/verifica-informativa, /servizi/analisi e
   /servizi/presenza-internazionale hanno in comune. Le
   istruzioni del Report (`oi-istruzioni-report-geostrategici.md`) dicono
   «la stessa struttura del Monitoraggio» e, pezzo per pezzo, «uguale a
   quello del Monitoraggio»: il titolo, il riquadro d'apertura, le
   intestazioni di sezione col segno OI, il principio 01 col suo schema,
   l'elenco apribile dei principi 02–04, il riquadro finale coi quattro
   dati e le tre card degli altri servizi. Report e Verifica («la stessa
   struttura del Report, con le stesse misure») hanno in comune anche le
   cinque fasi coi rombi e la riga dei tempi indicativi; Verifica e Studi di
   mercato («identici alla Verifica») i principi in griglia 2 × 2 col
   marchio al centro. Le sei fasi in tre tempi degli Studi di mercato
   stanno qui accanto alle cinque, perché sono la forma che un servizio a
   sei fasi può riprendere, e la Presenza internazionale («la stessa
   struttura degli Studi di mercato») riprende fasi, principi e riquadro
   finale. L'Import / Export riprende principi e riquadro finale, ma al
   posto dei tempi ha le sue sei «bolle di carico», che stanno qui accanto
   perché sono una forma di sei fasi come le altre. Stanno qui una volta
   sola, e le
   pagine passano soltanto i testi e i disegni. Server Components: nessuno
   di questi pezzi ha stato (i principi sono `<details>` nativi). */

/** Il titolo della pagina (`.r-testa`): Gelasio 48 senza segno OI, il
 *  sommario Gelasio 21 sonda, a destra servizio e area in mono, riga di 2
 *  pixel sotto. Numero e area si leggono dal catalogo di /servizi. */
export function TitoloServizio({ slug, titolo, sommario }: { slug: string; titolo: string; sommario: string }) {
  const area = AREE_PAGINA.find((a) => a.servizi.some((s) => s.slug === slug));
  const questo = area?.servizi.find((s) => s.slug === slug);
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-10 gap-y-4 border-b-2 border-ink pb-5">
      <div>
        {/* Sotto `sm` 40, come il motto dell'apertura: «Rischio
            reputazionale.» a 48 chiede 304 pixel di riga e a 320 di
            finestra ne trova 284 (misurato il 17/10/2026). */}
        <h1 className="m-0 font-serif text-[48px] leading-[1.05] font-normal max-sm:text-[40px]">{titolo}</h1>
        <p className="m-0 mt-2.5 max-w-[720px] font-serif text-[21px] leading-[1.45] text-sommesso">{sommario}</p>
      </div>
      <p className="m-0 font-mono text-meta leading-[1.8] text-sommesso uppercase sm:text-right">
        Servizio {questo?.numero} di {TOTALE_SERVIZI}
        <br />
        {area?.nome}
      </p>
    </header>
  );
}

/** Il riquadro d'apertura (`.s-hero.m-hero`): superficie, bordo
 *  inchiostro, riga rame in cima, colonne 0,9 : 1,25 (una sola sotto i
 *  900). A sinistra il testo sul fondo sfumato di /servizi, a destra il
 *  disegno animato su `--oi-sv-fondo` e sotto il disegno le quattro voci
 *  coi riquadri icona. `nota` è il secondo capoverso, Geist 16 sonda
 *  (`.m-hero .dk2`), che solo la Presenza internazionale ha. `rimando` è
 *  il collegamento accanto al bottone: «← Tutti i servizi» se non lo si
 *  passa; /osint, che non è un servizio, ci mette «Leggi i sei stadi ↓».
 *
 *  /cibernetica (17/10/2026) usa lo stesso riquadro con meno pezzi, e le
 *  prop qui sotto esistono per lei; senza, il riquadro è quello di sempre:
 *  - `testo` facoltativo e `nota` anche con collegamenti dentro: là non c'è
 *    la riga Gelasio 21, c'è solo il paragrafo Geist 16 coi rimandi al
 *    glossario;
 *  - `voci` facoltative: senza, il disegno arriva al fondo del riquadro
 *    (`.m-net { padding-bottom: 0 }` del modello);
 *  - `etichettaDisegno`, la scritta rame sopra il disegno («OI / Il ciclo
 *    della decisione» invece de «La cibernetica dell’informazione»);
 *  - `classeMotto`, la misura del motto (46, e 34 sotto i 900, nel modello
 *    della cibernetica).
 *
 *  /ai (17/10/2026) aggiunge `classeTesto`: nel suo modello la riga
 *  Gelasio 21 è inchiostro, non sonda, e ha 14 pixel in più sopra
 *  (`.cb-hero .dk`).
 *
 *  /chi-siamo (17/10/2026) ha un bottone solo, «Le basi della nostra
 *  indipendenza ↓» verso `#indipendenza`: `rimando={null}` toglie il
 *  collegamento accanto. */
export function AperturaServizio({
  nome,
  etichetta,
  motto,
  testo,
  nota,
  azione,
  contatto,
  didascalia,
  disegno,
  titoloVoci,
  voci,
  rimando = { testo: "← Tutti i servizi", href: "/servizi" },
  etichettaDisegno = "La cibernetica dell’informazione",
  classeMotto = "text-[48px] max-sm:text-[40px]",
  classeTesto = "text-sommesso",
}: {
  nome: string;
  etichetta: string;
  motto: ReactNode;
  testo?: string;
  nota?: ReactNode;
  azione: string;
  contatto: string;
  didascalia: string;
  disegno: ReactNode;
  titoloVoci?: string;
  voci?: { nome: string; icona: NomeIcona }[];
  rimando?: { testo: string; href: string } | null;
  etichettaDisegno?: string;
  classeMotto?: string;
  classeTesto?: string;
}) {
  return (
    <section
      aria-label={nome}
      className="mt-8 grid grid-cols-1 border border-t-4 border-ink border-t-segnale bg-surface min-[900px]:grid-cols-[minmax(0,0.9fr)_minmax(0,1.25fr)]"
    >
      <div className="flex flex-col justify-center gap-4 bg-[image:var(--oi-sv-apertura)] px-11 py-10 max-md:px-[22px] max-md:py-8">
        <p className={`m-0 ${ETICHETTA_ACCESA}`}>{etichetta}</p>
        <p className={`m-0 font-serif leading-[1.1] ${classeMotto}`}>{motto}</p>
        {testo ? <p className={`m-0 max-w-[460px] font-serif text-[21px] leading-[1.5] ${classeTesto}`}>{testo}</p> : null}
        {nota ? (
          <p className="m-0 max-w-[52ch] font-sans text-[16px] leading-[1.55] text-sommesso">{nota}</p>
        ) : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link href={contatto} className={PULSANTE}>
            {azione}
          </Link>
          {rimando ? (
            <Link href={rimando.href} className={RIMANDO}>
              {rimando.testo}
            </Link>
          ) : null}
        </div>
      </div>

      <figure className="relative m-0 flex flex-col gap-1.5 overflow-hidden bg-[var(--oi-sv-fondo)] px-7 pt-6 max-md:px-4">
        <p className="m-0 flex flex-wrap justify-between gap-x-4 gap-y-1.5 font-serif text-[18px] text-[var(--oi-sv-linea)]">
          <span className="font-sans text-voce font-semibold text-[var(--oi-sv-rame)]">{etichettaDisegno}</span>
          <span>{didascalia}</span>
        </p>
        {disegno}
        {voci ? (
          <div className="-mx-7 mt-1 border-t border-[color-mix(in_srgb,var(--oi-sv-linea)_22%,transparent)] px-7 pt-3.5 pb-[18px] max-md:-mx-4 max-md:px-4">
            <p className="m-0 mb-2.5 font-mono text-meta tracking-[0.08em] text-[var(--oi-sv-rame)] uppercase">
              {titoloVoci}
            </p>
            <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 min-[1000px]:grid-cols-4">
              {voci.map((v) => (
                <li
                  key={v.nome}
                  className="flex items-center gap-2.5 font-serif text-[16px] leading-[1.25] text-[var(--oi-sv-linea)]"
                >
                  <RiquadroIcona nome={v.icona} piccolo />
                  {v.nome}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </figure>
    </section>
  );
}

/** Il titolo di sezione (`.r-giorno.m-h`): segno OI + Gelasio 34, a
 *  destra il dato o il rimando, riga di 2 pixel sotto.
 *
 *  Sotto `sm` il dato va a capo sotto il titolo: misurato a 320, sulla
 *  stessa riga il titolo restava in 198-213 pixel (tre-quattro righe per
 *  «Come lavoriamo» e «Esplora gli altri servizi…») e «Tutti i servizi ↗»,
 *  che è `shrink-0`, sbordava di 49 pixel oltre la finestra. */
export function TestaSezione({ id, titolo, destra }: { id: string; titolo: string; destra: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b-2 border-ink pb-2.5 max-sm:flex-col max-sm:items-start max-sm:gap-1.5">
      <h2 id={id} className="m-0 font-serif text-[34px] leading-[1.15] font-normal max-sm:text-[28px]">
        <SegnoTitolo />
        {titolo}
      </h2>
      {destra}
    </div>
  );
}

/** Il dato a destra dell'intestazione: «3 passaggi», «4 principi». */
export function DatoSezione({ children }: { children: ReactNode }) {
  return <span className="shrink-0 font-mono text-meta text-sommesso">{children}</span>;
}

/** L'introduzione su due colonne (`.m-intro`): Gelasio 34 a sinistra,
 *  Gelasio 21 sonda a destra, appoggiate sul fondo. */
export function Introduzione({ sinistra, destra }: { sinistra: ReactNode; destra: string }) {
  return (
    <div className="mt-6 grid grid-cols-1 items-end gap-x-14 gap-y-4 min-[1000px]:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
      <p className="m-0 font-serif text-[34px] leading-[1.2] max-sm:text-[28px]">{sinistra}</p>
      <p className="m-0 font-serif text-[21px] leading-[1.45] text-sommesso">{destra}</p>
    </div>
  );
}

export type Fase = { titolo: string; testo: string; voci: [string, string, string] };

/** Le colonne delle fasi, condivise fra la griglia dei riquadri e la fila
 *  dei rombi: ogni rombo cade così al centro del suo riquadro. */
const COLONNE_FASI = "min-[1100px]:grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))]";

/** Le cinque fasi del Report e della Verifica (`.r-passi`): riquadri alti
 *  uguali, la 01 più larga (1,8 : 1 : 1 : 1 : 1), i cinque rombi sul bordo
 *  e le tre voci appoggiate in fondo; nella fase 05 i rombi delle voci
 *  sono rame pieni. I confini sono quelli del modello: cinque colonne coi
 *  rombi da 1100, due colonne senza rombi sotto (una sola sotto i 640, che
 *  il modello non ha: lì due colonne lasciano al titolo della fase meno di
 *  dieci caratteri per riga). */
export function CinqueFasi({ fasi }: { fasi: Fase[] }) {
  const ultima = fasi.length - 1;
  return (
    <div className="relative mt-8">
      {/* I cinque rombi sul bordo dei riquadri: il tratteggio va dal
          centro del primo (15%, la colonna 01 è larga) al centro
          dell'ultimo (9% dal bordo destro). Sotto i 1100 spariscono. */}
      <Rombi
        numeri={fasi.map((_, i) => String(i + 1).padStart(2, "0"))}
        className={`${COLONNE_FASI} gap-x-5 before:right-[9%] before:left-[15%] max-[1100px]:hidden`}
      />
      <ol className={`m-0 grid list-none grid-cols-1 gap-5 p-0 min-[640px]:max-[1100px]:grid-cols-2 ${COLONNE_FASI}`}>
        {fasi.map((f, i) => (
          <li key={f.titolo} className={RIQUADRO_FASE}>
            <p className={NUMERO_PASSO}>Fase {String(i + 1).padStart(2, "0")}</p>
            <h3 className="m-0 font-serif text-[24px] leading-[1.2] font-normal">{f.titolo}</h3>
            <p className={NOTA_PASSO}>{f.testo}</p>
            {/* Le voci in fondo: `mt-auto` le appoggia sul fondo in
                tutti i riquadri, che la griglia tiene alti uguali. */}
            <ul className="m-0 mt-auto flex list-none flex-col gap-2 border-t border-rule p-0 pt-3">
              {f.voci.map((v) => (
                <li
                  key={v}
                  className={`relative pl-[18px] font-serif text-[16px] leading-[1.3] before:absolute before:top-[6px] before:left-[2px] before:size-[9px] before:rotate-45 before:border before:content-[''] ${
                    i === ultima ? "before:border-segnale before:bg-segnale" : "before:border-mare before:bg-bg"
                  }`}
                >
                  {v}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

export type FaseTempo = Fase & { icona: NomeIcona };
export type Tempo = { etichetta: string; titolo: string; frase: string; fasi: [FaseTempo, FaseTempo] };

/** Le sei fasi in tre tempi di /servizi/analisi e
 *  /servizi/presenza-internazionale (`.ph` del modello
 *  `uploads/studi-di-mercato.html`): un riquadro solo, superficie e bordo
 *  inchiostro, diviso in tre colonne uguali da un filetto pieno. Ogni
 *  colonna è un tempo: la testata piena (blu, rame l'ultima; nello scuro
 *  il blu notte con la riga da 3 sotto, token `--oi-sv-tempo-*`) e le sue
 *  due fasi, separate da un tratteggio.
 *
 *  Le righe sono allineate fra le colonne con una griglia a tre righe in
 *  cui ogni colonna è `grid-rows-subgrid`: testata, prima fase e seconda
 *  fase sono alte quanto la più alta delle tre colonne, quindi il
 *  tratteggio cade alla stessa quota in tutte. Sotto i 1100 le colonne si
 *  impilano e ognuna torna una pila normale (`flex-col`): un subgrid che
 *  occupa una riga sola non può ospitare tre figli, li sovrapporrebbe.
 *
 *  Nell'ultimo tempo numeri e rombi delle voci sono rame, e le fasi hanno
 *  il fondo velato del 6% di rame. Sotto il riquadro, la riga che va
 *  dall'ipotesi alla scelta (`esito`). Nessuna linea del tempo. */
export function FasiInTempi({
  tempi,
  esito,
}: {
  tempi: Tempo[];
  esito: { da: string; a: string; nota: string };
}) {
  const ultimo = tempi.length - 1;
  return (
    <>
      <div className="mt-8 grid grid-cols-1 border border-ink bg-surface min-[1100px]:grid-cols-3 min-[1100px]:grid-rows-[auto_auto_auto]">
        {tempi.map((t, i) => {
          const ora = i === ultimo;
          return (
            <div
              key={t.titolo}
              className={`flex flex-col border-ink min-[1100px]:row-span-3 min-[1100px]:grid min-[1100px]:grid-rows-subgrid ${
                i === 0 ? "" : "border-t min-[1100px]:border-t-0 min-[1100px]:border-l"
              }`}
            >
              <header
                className={`border-b-[length:var(--oi-sv-tempo-riga)] px-[26px] pt-[22px] pb-5 text-[var(--oi-sv-su-tempo)] max-sm:px-[18px] ${
                  ora
                    ? "border-b-segnale bg-[var(--oi-sv-tempo-ora)]"
                    : "border-b-mare bg-[var(--oi-sv-tempo)]"
                }`}
              >
                <p className="m-0 font-mono text-[11.5px] tracking-[0.1em] uppercase opacity-75">{t.etichetta}</p>
                <p
                  className={`m-0 mt-1.5 font-serif text-[30px] leading-[1.1] ${
                    ora ? "text-[var(--oi-sv-titolo-tempo-ora)]" : ""
                  }`}
                >
                  {t.titolo}
                </p>
                <p className="m-0 mt-2 font-sans text-[14.5px] leading-[1.45] opacity-85">{t.frase}</p>
              </header>
              {t.fasi.map((f, j) => {
                const numero = String(i * 2 + j + 1).padStart(2, "0");
                return (
                  <article
                    key={f.titolo}
                    className={`grid grid-cols-[76px_minmax(0,1fr)] gap-1 py-[26px] pr-[26px] pl-[18px] ${
                      j > 0 ? "border-t border-dashed border-sommesso" : ""
                    } ${ora ? "bg-[color-mix(in_srgb,var(--color-segnale)_6%,var(--color-surface))]" : ""}`}
                  >
                    <p
                      className={`m-0 font-serif text-[56px] leading-[0.9] ${
                        ora ? "text-segnale" : "text-mare"
                      }`}
                    >
                      {numero}
                    </p>
                    <div className="flex min-w-0 flex-col gap-2">
                      <div className="flex items-center gap-2.5">
                        <RiquadroIcona nome={f.icona} minimo />
                        <p className="m-0 font-mono text-[11.5px] tracking-[0.08em] text-sommesso uppercase">
                          Fase {numero}
                        </p>
                      </div>
                      <h3 className="m-0 font-serif text-[21px] leading-[1.2] font-normal">{f.titolo}</h3>
                      <p className="m-0 font-sans text-sommario leading-[1.5] text-sommesso">{f.testo}</p>
                      <ul className="m-0 mt-1 flex list-none flex-col gap-[5px] p-0">
                        {f.voci.map((v) => (
                          <li
                            key={v}
                            className={`relative pl-5 font-mono text-meta text-ink before:absolute before:top-1 before:left-0.5 before:size-[9px] before:rotate-45 before:border before:content-[''] ${
                              ora ? "before:border-segnale before:bg-segnale" : "before:border-mare"
                            }`}
                          >
                            {v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                );
              })}
            </div>
          );
        })}
      </div>
      {/* La riga sotto il riquadro (`.ph-esito`): l'ipotesi blu, una linea
          piena con la punta che prende tutto lo spazio, la scelta rame. */}
      <p className="m-0 mt-[18px] flex items-center gap-3.5 font-mono text-meta tracking-[0.1em] uppercase">
        <span className="text-mare">{esito.da}</span>
        <span
          aria-hidden
          className="relative h-px flex-1 bg-ink after:absolute after:-top-1 after:right-0 after:border-y-4 after:border-l-[7px] after:border-y-transparent after:border-l-ink after:content-['']"
        />
        <span className="font-semibold text-segnale">{esito.a}</span>
        <span className="tracking-[0.06em] text-sommesso">{esito.nota}</span>
      </p>
    </>
  );
}

export type Bolla = FaseTempo & {
  /** «A che cosa serve»: l'esito della fase, nel timbro in fondo. */
  serve: string;
};

/** Le sei fasi di /servizi/import-export come «bolle di carico» (`.bl-g`
 *  del modello `uploads/import-export.html`, § 3 delle istruzioni): non la
 *  tabella in tre tempi delle sorelle, ma sei documenti di carico tutti
 *  visibili, tre per riga a 24 di passo da 1100, uno per riga sotto.
 *
 *  Ogni bolla (`.bl`): superficie e filetto d'inchiostro, nessuna riga
 *  rame in cima. La testata sul fondo dei disegni (`--p0`, cioè
 *  `--oi-sv-fondo`: azzurro chiarissimo, blu notte nello scuro) chiusa da
 *  un tratteggio, col riquadro icona, «Fase 0N / 06» e a destra il codice
 *  (`sigla·0N`) in un'etichetta col filetto. Nel corpo il titolo, il testo,
 *  «Che cosa esaminiamo» e le tre voci come righe di un elenco di carico:
 *  la lettera, la voce e la casella da 10 vuota. In fondo il timbro, a
 *  doppio filetto rame (il bordo da 1,5 più un contorno da 1 staccato di
 *  2), con l'esito in Gelasio 17 blu.
 *
 *  L'ultima è la bolla d'arrivo (`.bl.ora`): filetto rame, testata velata
 *  di rame (12%) col tratteggio rame, il codice pieno rame, le caselle
 *  piene e il timbro velato (6%) con l'esito rame. Le bolle della stessa
 *  riga sono alte uguali (la griglia le stira, il corpo è `flex-1`), così i
 *  timbri cadono alla stessa quota. */
export function BolleDiCarico({ bolle, sigla }: { bolle: Bolla[]; sigla: string }) {
  const ultima = bolle.length - 1;
  const totale = String(bolle.length).padStart(2, "0");
  return (
    <ol className="m-0 mt-8 grid list-none grid-cols-1 gap-6 p-0 min-[1100px]:grid-cols-3">
      {bolle.map((b, i) => {
        const ora = i === ultima;
        const numero = String(i + 1).padStart(2, "0");
        return (
          <li key={b.titolo} className={`flex flex-col border bg-surface ${ora ? "border-segnale" : "border-ink"}`}>
            <header
              className={`flex items-center gap-3 border-b border-dashed px-5 py-3.5 ${
                ora
                  ? "border-b-segnale bg-[color-mix(in_srgb,var(--color-segnale)_12%,var(--color-surface))]"
                  : "border-b-ink bg-[var(--oi-sv-fondo)]"
              }`}
            >
              <RiquadroIcona nome={b.icona} piccolo />
              <p className="m-0 font-mono text-meta tracking-[0.08em] text-segnale uppercase">
                Fase {numero}
                <span className="text-sommesso"> / {totale}</span>
              </p>
              <p
                className={`m-0 ml-auto border px-2 py-[3px] font-mono text-[11px] tracking-[0.1em] ${
                  ora ? "border-segnale bg-segnale text-bg" : "border-ink"
                }`}
              >
                {sigla}·{numero}
              </p>
            </header>
            <div className="flex flex-1 flex-col gap-2 px-5 pt-5 pb-4">
              <h3 className="m-0 font-serif text-[24px] leading-[1.2] font-normal">{b.titolo}</h3>
              <p className="m-0 font-sans text-sommario leading-[1.5] text-sommesso">{b.testo}</p>
              <p className="m-0 mt-2 font-mono text-[10.5px] tracking-[0.1em] text-sommesso uppercase">
                Che cosa esaminiamo
              </p>
              <ol className="m-0 list-none border-t border-ink p-0">
                {b.voci.map((v, j) => (
                  <li
                    key={v}
                    className="grid grid-cols-[26px_minmax(0,1fr)_14px] items-center gap-2.5 border-b border-rule py-2 font-sans text-sommario"
                  >
                    <i className="font-mono text-[11px] text-sommesso not-italic">{"ABC"[j]}</i>
                    <span>{v}</span>
                    <b
                      aria-hidden
                      className={`size-3 border ${ora ? "border-segnale bg-segnale" : "border-mare"}`}
                    />
                  </li>
                ))}
              </ol>
            </div>
            <footer
              className={`mx-5 mb-5 flex flex-col gap-1 border-[1.5px] border-segnale px-3.5 py-3 outline outline-1 outline-offset-2 outline-segnale ${
                ora ? "bg-[color-mix(in_srgb,var(--color-segnale)_6%,var(--color-surface))]" : ""
              }`}
            >
              <p className="m-0 font-mono text-[10.5px] tracking-[0.1em] text-segnale uppercase">A che cosa serve</p>
              <p className={`m-0 font-serif text-[17px] leading-[1.3] ${ora ? "text-segnale" : "text-mare"}`}>
                {b.serve}
              </p>
            </footer>
          </li>
        );
      })}
    </ol>
  );
}

export type PrincipioCard = {
  etichetta: string;
  titolo: string;
  frase: string;
  testo: string;
  disegno: ReactNode;
  /** La riga «Nel processo» in fondo alla card (`.vp-np` del modello di
   *  /servizi/import-export): le fasi in cui il principio si applica,
   *  numero e nome. Le altre pagine non la passano e non cambiano. */
  processo?: string;
};

/** I principi del metodo in griglia 2 × 2 col marchio OI al centro, di
 *  /servizi/verifica-informativa, /servizi/analisi e
 *  /servizi/presenza-internazionale (`.vp-g4` dei loro
 *  modelli, «costruiti identici»): due colonne uguali a 28 di passo, le
 *  card della stessa riga alte uguali (la griglia le stira, e il disegno
 *  di ognuna è assoluto dentro la sua colonna, quindi non ne decide
 *  l'altezza). Ogni card (`.vp-c`) è divisa 0,8 : 1,2 fra il disegno, su
 *  `--p0` e alto quanto la card, e il testo; nelle card di destra
 *  (`.vp-c.dx`) l'ordine si rovescia, così i testi stanno verso il centro
 *  e il lato verso il centro ha 64 di rientro per il marchio. Sotto, il
 *  motto fra due tratteggi rame.
 *
 *  La soglia è quella del modello (`@media (max-width:1100px)`), la stessa
 *  delle fasi: da 1100 due colonne e il marchio, sotto una card per riga e
 *  niente marchio. Lì quei 64 non servono più e il rientro torna 26 per
 *  lato (18 sotto i 640): sono le sole misure che il modello non ha.
 *  I principi vanno passati nell'ordine della griglia: 01 e 03 a sinistra,
 *  02 e 04 a destra. */
export function PrincipiInGriglia({ principi, motto }: { principi: PrincipioCard[]; motto: string }) {
  return (
    <>
      <div className="relative mt-7 grid grid-cols-1 gap-7 min-[1100px]:grid-cols-2">
        {principi.map((p, i) => {
          const destra = i % 2 === 1;
          return (
            <article
              key={p.etichetta}
              className={`grid overflow-hidden border border-t-4 border-ink border-t-segnale bg-surface ${
                destra ? "grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]" : "grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]"
              }`}
            >
              <div className={`relative min-h-full bg-[var(--oi-sv-fondo)] ${destra ? "order-2" : ""}`}>
                {p.disegno}
              </div>
              <div
                className={`flex flex-col gap-2 px-[26px] pt-[22px] pb-6 max-sm:px-[18px] ${
                  destra ? "min-[1100px]:pl-16" : "min-[1100px]:pr-16"
                }`}
              >
                <p className={NUMERO_PASSO}>{p.etichetta}</p>
                <h3 className="m-0 font-serif text-[24px] leading-[1.2] font-normal">{p.titolo}</h3>
                <p className="m-0 font-serif text-[18px] leading-[1.4] text-mare">{p.frase}</p>
                <p className="m-0 mt-0.5 font-serif text-[16px] leading-[1.6] text-ink">{p.testo}</p>
                {p.processo ? (
                  <p className="m-0 mt-1.5 border-t border-dashed border-rule pt-2.5 font-mono text-[11.5px] tracking-[0.06em] text-mare uppercase">
                    <span className="mr-2 text-segnale">Nel processo</span>
                    {p.processo}
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
        {/* Il marchio al centro (`.vp-oi`): un cerchio da 104 su carta col
            filetto d'inchiostro (106 in tutto: nel modello il bordo sta
            fuori dai 104) e un alone di carta da 12, che lo stacca
            dagli angoli delle quattro card; dentro il segno OI 52 × 42 a
            colori, O e I nel mare e il triangolo rame. */}
        <span
          aria-hidden
          className="absolute top-1/2 left-1/2 z-[3] hidden size-[106px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-ink bg-bg shadow-[0_0_0_12px_var(--color-bg)] min-[1100px]:grid"
        >
          <LogoOI variant="segno" decorative accento="fill-segnale" className="h-[42px] w-[52px] text-mare" />
        </span>
      </div>
      <p className="m-0 mt-[22px] flex items-center gap-3.5 font-mono text-meta tracking-[0.1em] text-segnale uppercase before:flex-1 before:border-t before:border-dashed before:border-segnale before:content-[''] after:flex-1 after:border-t after:border-dashed after:border-segnale after:content-['']">
        <span>{motto}</span>
      </p>
    </>
  );
}

export type Principio = { n: string; titolo: string; sotto: string; testo: string; aperto: boolean };

/** I principi del metodo (`.m-pg`): il principio 01 a sinistra, i 02–04
 *  apribili a destra, colonne 1,15 : 1 che finiscono alla stessa altezza
 *  (`items-stretch`, e il disegno del 01 scende in fondo col suo
 *  `mt-auto`). Sotto i 1000 una colonna sola.
 *
 *  Il principio 01 (`.m-p1`): etichetta rame, titolo Gelasio 34, il
 *  sottotitolo blu se c'è (il Report non ce l'ha), il testo Gelasio 18,
 *  lo schema a tutta larghezza e sotto, sullo stesso fondo, la legenda se
 *  c'è (`.r-leg` del Report) e la striscia del motto. La legenda va a una
 *  colonna sotto i 360, che il modello non prevede: a 320 «Confronto» in
 *  Gelasio 18 è più largo della sua colonna. */
export function PrincipiMetodo({
  etichetta,
  titolo,
  sotto,
  testo,
  schema,
  legenda,
  motto,
  principi,
}: {
  etichetta: string;
  titolo: string;
  sotto?: string;
  testo: string;
  schema: ReactNode;
  legenda?: { titolo: string; testo: string }[];
  motto: string;
  principi: Principio[];
}) {
  return (
    <div className="mt-7 grid grid-cols-1 items-stretch gap-6 min-[1000px]:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <article className="flex flex-col gap-3 overflow-hidden border border-t-4 border-ink border-t-segnale bg-surface px-[30px] pt-7 max-md:px-[22px]">
        <p className="m-0 font-mono text-meta tracking-[0.08em] text-segnale uppercase">{etichetta}</p>
        <h3 className="m-0 font-serif text-[34px] leading-[1.15] font-normal max-sm:text-[28px]">{titolo}</h3>
        {sotto ? <p className="m-0 font-serif text-[21px] leading-[1.4] text-mare">{sotto}</p> : null}
        <p className="m-0 font-serif text-[18px] leading-[1.6]">{testo}</p>
        {schema}
        {legenda ? (
          <ol className="m-0 -mx-[30px] grid list-none grid-cols-3 gap-[18px] border-t border-[color-mix(in_srgb,var(--oi-sv-linea)_20%,transparent)] bg-[var(--oi-sv-fondo)] px-[30px] pt-3.5 pb-4 max-md:-mx-[22px] max-md:px-[22px] max-[360px]:grid-cols-1">
            {legenda.map((l, i) => (
              <li key={l.titolo} className="flex flex-col gap-[3px]">
                <i className="font-mono text-meta text-[var(--oi-sv-rame)] not-italic">
                  {String(i + 1).padStart(2, "0")}
                </i>
                <b className="font-serif text-[18px] font-normal text-[var(--oi-sv-linea)]">{l.titolo}</b>
                <span className="font-sans text-[13px] leading-[1.4] text-[var(--oi-sv-linea)] opacity-80">
                  {l.testo}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
        <p className="m-0 -mx-[30px] border-t border-[color-mix(in_srgb,var(--oi-sv-linea)_20%,transparent)] bg-[var(--oi-sv-fondo)] px-[30px] py-3 font-mono text-meta tracking-[0.1em] text-[var(--oi-sv-rame)] uppercase max-md:-mx-[22px] max-md:px-[22px]">
          {motto}
        </p>
      </article>

      <div className="flex flex-col border-t-2 border-ink">
        {principi.map((p) => (
          <details key={p.n} open={p.aperto} className="group border-b border-ink">
            <summary
              className={`flex cursor-pointer list-none items-baseline gap-[18px] px-1 py-5 [&::-webkit-details-marker]:hidden ${FUOCO_HOME}`}
            >
              <i className="font-mono text-meta text-segnale not-italic">{p.n}</i>
              <span className="font-serif text-[26px] leading-[1.2] transition-colors group-hover:text-mare max-sm:text-[22px]">
                {p.titolo}
              </span>
              <b
                aria-hidden
                className="ml-auto font-normal text-[24px] text-mare transition-transform group-open:rotate-45"
              >
                +
              </b>
            </summary>
            <p className="m-0 pr-1 pl-[46px] font-serif text-[21px] leading-[1.4] text-mare max-sm:pl-1">{p.sotto}</p>
            <p className="m-0 mt-2.5 pr-1 pb-[22px] pl-[46px] font-serif text-[18px] leading-[1.6] max-sm:pl-1">
              {p.testo}
            </p>
          </details>
        ))}
      </div>
    </div>
  );
}

const DATI = [
  { valore: "7 / 7", spiega: "Sempre raggiungibili" },
  { valore: "48h", spiega: "Prima risposta" },
  { valore: "NDA", spiega: "Sotto accordo" },
  { valore: "Riservato", spiega: "Numero limitato di clienti" },
];

/** Il riquadro finale (`.m-cta`): superficie, bordo inchiostro, riga rame
 *  in cima, colonne 1,2 : 1. A sinistra etichetta, domanda, testo, i due
 *  rimandi e, se ci sono, la nota dopo i rimandi e la riga dei tempi
 *  (`.r-tempi`: «Tempi indicativi» mono rame, la durata Gelasio 21 blu,
 *  e in coda «in media», o la `codaTempi` che la pagina passa: la
 *  Presenza internazionale dice «a seconda del paese»); a
 *  destra i quattro dati in griglia 2 × 2, uguali in tutti i servizi.
 *
 *  L'etichetta qui è `m-0`, e il riquadro esce 26 px più basso dei
 *  modelli: di proposito. Nei sei HTML (`monitoraggio 01`, `report-
 *  geostrategici`, `verifica-informativa 01`, `studi-di-mercato`,
 *  `presenza-internazionale`, `import-export`) `.m-cta .oi-k` non ha
 *  nessuna regola di margine, quindi il `<p>` tiene quello del browser
 *  (1em a 13 px, sopra e sotto), che nella colonna flex non collassa.
 *  È una dimenticanza, non una misura: `.m-cta h2` e `.m-cta .sub`
 *  azzerano il margine esplicitamente, e così ogni altra `.oi-k` dei
 *  modelli. Copiato, darebbe 49 px d'aria sopra l'etichetta contro i 36
 *  sotto, e 25 fra etichetta e domanda contro i 12 fra domanda e testo:
 *  l'etichetta si staccherebbe dal titolo che annuncia. */
export function RiquadroFinale({
  id,
  etichetta,
  titolo,
  testo,
  contatto,
  nota,
  tempi,
  codaTempi = "in media",
  azione = "Richiedi un inquadramento ↗",
  rimando = "Accordo di riservatezza ↗",
}: {
  id: string;
  etichetta: string;
  titolo: string;
  testo: string;
  contatto: string;
  nota?: string;
  tempi?: string;
  codaTempi?: string;
  /** Il testo del bottone e quello del rimando all'NDA: la Cibernetica
   *  aziendale dice «Progettiamo il tuo sistema ↗» e «Le modalità di
   *  riservatezza ↗», le altre pagine tengono i valori di sempre. */
  azione?: string;
  rimando?: string;
}) {
  return (
    <section
      aria-labelledby={id}
      className="mt-[60px] grid grid-cols-1 items-center gap-x-14 gap-y-6 border border-t-4 border-ink border-t-segnale bg-surface px-11 py-9 max-md:px-[22px] max-md:py-7 min-[1000px]:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]"
    >
      <div className="flex flex-col gap-3">
        <p className={`m-0 ${ETICHETTA_ACCESA}`}>{etichetta}</p>
        <h2 id={id} className="m-0 font-serif text-[34px] leading-[1.15] font-normal max-sm:text-[28px]">
          {titolo}
        </h2>
        <p className="m-0 font-sans text-sommario leading-[1.55] text-sommesso">{testo}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link href={contatto} className={PULSANTE}>
            {azione}
          </Link>
          <Link href="/nda" className={RIMANDO}>
            {rimando}
          </Link>
        </div>
        {nota ? <p className="m-0 font-sans text-sommario leading-[1.55] text-sommesso">{nota}</p> : null}
        {tempi ? (
          <p className="m-0 mt-1 flex items-baseline gap-2.5 font-sans text-sommario text-sommesso">
            <span className="font-mono text-meta tracking-[0.08em] text-segnale uppercase">Tempi indicativi</span>
            <b className="font-serif text-[21px] font-normal text-mare">{tempi}</b> {codaTempi}
          </p>
        ) : null}
      </div>
      <dl className="m-0 grid grid-cols-2 border-t border-l border-t-ink border-l-rule">
        {DATI.map((d) => (
          <div key={d.valore} className="border-r border-b border-rule px-[18px] py-4">
            <dt className="font-serif text-[30px] leading-[1.1] text-mare max-sm:text-[24px]">{d.valore}</dt>
            <dd className="m-0 mt-1.5 font-mono text-meta tracking-[0.06em] text-sommesso uppercase">{d.spiega}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** «Esplora gli altri servizi dell'Osservatorio»: tre card del catalogo
 *  (`CardServizio`), tre per riga da 1100, due fra 700 e 1099, una sotto.
 *
 *  Dal 19/10/2026 le tre card ruotano su tutti i quattordici servizi, meno
 *  quello della pagina: prima ogni pagina le sceglieva a mano (`scelti`, le
 *  card del suo modello) o mostrava gli altri della stessa area. Le pagine
 *  sono statiche, quindi il sorteggio lo fa il browser dopo l'idratazione
 *  (`AltriServiziARotazione`); la scelta di prima resta come stato
 *  iniziale, cioè quello che c'è nell'HTML del build (`PRIMA_SCELTA_ALTRI`
 *  in lib/sorteggio-servizi.ts). `slug` vuoto (/contatti) non esclude
 *  niente. */
export function AltriServizi({
  slug,
  id,
  titolo = "Esplora gli altri servizi dell’Osservatorio",
}: {
  slug: string;
  id: string;
  /** /contatti (19/10/2026) chiude con le stesse tre card sotto un altro
   *  titolo, «Su che cosa possiamo lavorare insieme»; senza, quello di
   *  sempre. */
  titolo?: string;
}) {
  const escludi = slug ? [slug] : [];
  const iniziali = PRIMA_SCELTA_ALTRI[slug || "contatti"] ?? sorteggioFisso(escludi);
  return (
    <section aria-labelledby={id} className="mt-[60px]">
      <TestaSezione
        id={id}
        titolo={titolo}
        destra={
          <Link href="/servizi" className={`shrink-0 ${RIMANDO}`}>
            Tutti i servizi ↗
          </Link>
        }
      />
      <div className="mt-6 grid grid-cols-1 gap-6 min-[700px]:max-[1100px]:grid-cols-2 min-[1100px]:grid-cols-3">
        <AltriServiziARotazione iniziali={iniziali} escludi={escludi} />
      </div>
    </section>
  );
}

/** Ripiego per uno slug che non ha la sua prima scelta: i primi tre del
 *  catalogo che non sono esclusi. Deterministico, perché è l'HTML del build. */
function sorteggioFisso(escludi: string[]): string[] {
  return SERVIZI_CATALOGO.filter((s) => !escludi.includes(s.slug))
    .slice(0, 3)
    .map((s) => s.slug);
}
